// EXPERIMENT #171270395
import React from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';

import { Formik } from 'formik';

import Button from 'components/button';
import ClockIllustration from 'icons/illo-clock-homepage.svg';
import { getAnswersFromStorage, storeAnswerToStorage } from '../storage_helpers';
import {
  trackQuestionAnswered,
  trackBackButtonClicked,
  trackIntroductionGetStartedClicked,
} from '../tracking_helpers';
import SurveyQuestion from './survey_question';
import QUESTIONS from '../constants/configurations';
import IntroductionScreen from './introduction_screen';
import Header from './Header';
import LpHeader from './LpHeader';

const ProcessingScreen = () => (
  <div className="processing-screen text-center">
    <img alt="Clock illustration" className="margin-bottom-40px" src={ClockIllustration} />
    <h4>Thanks!</h4>
    <div>Just one moment.</div>
  </div>
);

class SurveyManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentQuestion: 'RUN_PAYROLL',
      showProcessingScreen: false,
      hasUserStartedSurvey: false,
      showIntroductionScreen: this.props.showIntroductionScreen,
    };
  }

  PROCESSING_SCREEN_DELAY = 1000;

  setNextState = (nextQuestion, showProcessingScreenWithDelay) => {
    if (!nextQuestion) {
      if (showProcessingScreenWithDelay) {
        this.setState({ showProcessingScreen: true });
        setTimeout(() => {
          this.props.onSurveyFinish();
        }, this.PROCESSING_SCREEN_DELAY);
      } else {
        this.props.onSurveyFinish();
      }
    }

    this.setState({ currentQuestion: nextQuestion });
  };

  getNextState = (selectedAnswer, currentQuestion) => {
    const nextQuestionOptions = QUESTIONS[currentQuestion].next;

    let nextQuestionKey = nextQuestionOptions.default;
    if (selectedAnswer in nextQuestionOptions) {
      nextQuestionKey = nextQuestionOptions[selectedAnswer];
    }

    return nextQuestionKey;
  };

  getNextStateFromSession() {
    const savedAnswers = getAnswersFromStorage();
    let currentQuestion = 'RUN_PAYROLL';

    while (currentQuestion && savedAnswers[currentQuestion]) {
      currentQuestion = this.getNextState(savedAnswers[currentQuestion], currentQuestion);
    }
    return currentQuestion;
  }

  updateQuestionState(showProcessingScreenWithDelay) {
    const currentQuestionBasedOnSession = this.getNextStateFromSession();
    if (this.state.currentQuestion !== currentQuestionBasedOnSession) {
      this.setNextState(currentQuestionBasedOnSession, showProcessingScreenWithDelay);
    }
  }

  updateIntroductionScreenState() {
    const savedAnswers = getAnswersFromStorage();
    if (
      this.state.showIntroductionScreen !==
      (this.props.showIntroductionScreen && !savedAnswers.hasInteractedWithSurvey)
    ) {
      this.setState({
        showIntroductionScreen:
          this.props.showIntroductionScreen && !savedAnswers.hasInteractedWithSurvey,
      });
    }
  }

  componentDidMount() {
    const savedAnswers = getAnswersFromStorage();
    this.setState({ hasUserStartedSurvey: Boolean(savedAnswers.RUN_PAYROLL) });
    this.updateQuestionState(false);
  }

  componentDidUpdate() {
    this.updateQuestionState(true);
    this.updateIntroductionScreenState();
  }

  render() {
    const onAnswerSelected = () => {
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      this.setState({ hasUserStartedSurvey: false });
      this.updateQuestionState(true);
    };

    const onBackClick = () => {
      const prevQuestionKey = QUESTIONS[this.state.currentQuestion].prev;
      trackBackButtonClicked(this.state.currentQuestion);

      if (prevQuestionKey) {
        this.setState({ currentQuestion: prevQuestionKey });
        storeAnswerToStorage(prevQuestionKey, null);
      }

      this.setState({ hasUserStartedSurvey: false });
    };

    const onOtherAnswerSelected = () => {
      const answerValue = QUESTIONS[this.state.currentQuestion].other_answer.key;
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      storeAnswerToStorage(this.state.currentQuestion, answerValue);
      trackQuestionAnswered(this.state.currentQuestion, answerValue);
      onAnswerSelected();
    };

    const onIntroductionGetStartedClicked = () => {
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      trackIntroductionGetStartedClicked();
      this.updateIntroductionScreenState();
    };

    return (
      <Formik>
        <div className="survey-manager">
          {this.state.showIntroductionScreen && (
            <IntroductionScreen onClick={onIntroductionGetStartedClicked} />
          )}
          {this.state.showProcessingScreen && <ProcessingScreen />}
          {this.state.currentQuestion && !this.state.showIntroductionScreen && (
            <div className="text-left">
              <div className="lp-survey-step">
                {this.props.isLP ? (
                  <LpHeader order={QUESTIONS[this.state.currentQuestion].order} />
                ) : (
                  <Header
                    hasUserStartedSurvey={this.state.hasUserStartedSurvey}
                    headerText={QUESTIONS[this.state.currentQuestion].header}
                  />
                )}

                <SurveyQuestion
                  questionKey={this.state.currentQuestion}
                  onAnswerSelected={onAnswerSelected}
                  isLP={this.props.isLP}
                />
              </div>

              <div>
                {QUESTIONS[this.state.currentQuestion].prev && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-6 text-left lp-survey-back-link"
                    label="Back"
                    onClick={onBackClick}
                  />
                )}
                {QUESTIONS[this.state.currentQuestion].other_answer && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-6 text-right"
                    label={QUESTIONS[this.state.currentQuestion].other_answer.text}
                    onClick={onOtherAnswerSelected}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Formik>
    );
  }
}

SurveyManager.propTypes = {
  onSurveyFinish: PropTypes.func.isRequired,
  showIntroductionScreen: PropTypes.bool,
  isLP: PropTypes.bool,
};

SurveyManager.defaultProps = {
  showIntroductionScreen: false,
  isLP: false,
};

export default hot(module)(SurveyManager);
